import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, UserPlus, Users, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * PULA LEARN — employer portal: create organisation, reserve team seats
 * (free pre-launch, converts to paid at launch), assign learners, view progress.
 */

export default function EmployerPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [bundles, setBundles] = useState([]);
  const [seatForm, setSeatForm] = useState({ bundle_slug: "", quantity: 5 });
  const [assign, setAssign] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: dash }, { data: b }] = await Promise.all([
        api.get("/learn/employer/dashboard"),
        api.get("/learn/bundles"),
      ]);
      setData(dash);
      setBundles(b.bundles || []);
      if (!seatForm.bundle_slug && b.bundles?.length) setSeatForm((f) => ({ ...f, bundle_slug: b.bundles[0].slug }));
    } catch (err) {
      if (err?.response?.status === 401) navigate("/login");
      else toast.error("Could not load employer portal.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const createOrg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/learn/employer/organisation", { name: orgName });
      toast.success("Organisation created.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create organisation.");
    } finally { setBusy(false); }
  };

  const reserve = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post("/learn/employer/seats", seatForm);
      toast.success(res.message);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reserve seats.");
    } finally { setBusy(false); }
  };

  const doAssign = async (seatId) => {
    const email = (assign[seatId] || "").trim();
    if (!email) return;
    try {
      await api.post(`/learn/employer/seats/${seatId}/assign`, { learner_email: email });
      toast.success(`Seat assigned to ${email}.`);
      setAssign({ ...assign, [seatId]: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assignment failed.");
    }
  };

  const unassign = async (seatId) => {
    try {
      await api.post(`/learn/employer/seats/${seatId}/unassign`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unassign.");
    }
  };

  if (!data) return <main className="py-24 text-center text-sm text-gray-500" data-testid="employer-loading">Loading…</main>;
  const org = data.organisation;

  return (
    <main className="bg-white min-h-[70vh]" data-testid="employer-page">
      <section className="mv-green-bg text-white py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-3"><Building2 size={30} /> Employer Portal</h1>
          <p className="text-sm md:text-base text-white/80 max-w-2xl">
            Enrol your whole team at once and track everyone's progress in one place.
            {!data.cpd_accredited && " Pre-launch: reserving seats is free — they convert to paid enrolments when accredited enrolment opens."}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        {!org ? (
          <form onSubmit={createOrg} className="max-w-md rounded-xl border mv-hairline p-6" data-testid="org-create-form">
            <h2 className="font-bold text-lg mb-1">Create your organisation</h2>
            <p className="text-sm text-gray-600 mb-4">One organisation per manager account.</p>
            <input value={orgName} onChange={(e) => setOrgName(e.target.value)} required minLength={2}
              placeholder="e.g. Sunrise Care Ltd" data-testid="org-name-input"
              className="border mv-hairline rounded-lg px-3 py-2.5 text-sm w-full mb-4" />
            <button disabled={busy} data-testid="org-create-btn"
              className="btn-green px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60">
              {busy ? "Creating…" : "Create organisation"}
            </button>
          </form>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="text-xl font-bold" data-testid="org-name">{org.name}</h2>
              <form onSubmit={reserve} className="flex flex-wrap items-end gap-3" data-testid="seat-reserve-form">
                <label className="text-xs text-gray-500">
                  Bundle
                  <select value={seatForm.bundle_slug} onChange={(e) => setSeatForm({ ...seatForm, bundle_slug: e.target.value })}
                    data-testid="seat-bundle-select" className="block mt-1 border mv-hairline rounded-lg px-3 py-2 text-sm bg-white">
                    {bundles.map((b) => <option key={b.slug} value={b.slug}>{b.title} — £{b.price_gbp?.toFixed(2)}</option>)}
                  </select>
                </label>
                <label className="text-xs text-gray-500">
                  Seats
                  <input type="number" min={1} max={500} value={seatForm.quantity}
                    onChange={(e) => setSeatForm({ ...seatForm, quantity: parseInt(e.target.value) || 1 })}
                    data-testid="seat-quantity-input" className="block mt-1 border mv-hairline rounded-lg px-3 py-2 text-sm w-24" />
                </label>
                <button disabled={busy} data-testid="seat-reserve-btn"
                  className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 inline-flex items-center gap-1.5">
                  <Plus size={15} /> {data.cpd_accredited ? "Purchase seats" : "Reserve seats — free"}
                </button>
              </form>
            </div>

            <div className="rounded-xl border mv-hairline overflow-x-auto" data-testid="seats-table-wrap">
              <table className="w-full text-sm" data-testid="seats-table">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Bundle</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Learner</th>
                    <th className="px-4 py-3 font-semibold">Progress</th>
                    <th className="px-4 py-3 font-semibold">Assign</th>
                  </tr>
                </thead>
                <tbody>
                  {data.seats.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No seats yet — reserve your first team seats above.
                    </td></tr>
                  )}
                  {data.seats.map((s) => (
                    <tr key={s.id} className="border-t mv-hairline" data-testid={`seat-row-${s.id}`}>
                      <td className="px-4 py-3">{s.bundle_slug}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${s.status === "assigned" ? "bg-emerald-100 text-emerald-900" : "bg-gray-100 text-gray-600"}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{s.learner_email || "—"}</td>
                      <td className="px-4 py-3" data-testid={`seat-progress-${s.id}`}>
                        {s.progress ? (
                          s.progress.completed_at
                            ? <span className="text-emerald-700 font-semibold">Complete</span>
                            : <span>{s.progress.modules_done}/{s.progress.modules_total} modules</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {s.learner_email ? (
                          <button onClick={() => unassign(s.id)} data-testid={`seat-unassign-${s.id}`}
                            className="text-xs text-red-600 underline">Unassign</button>
                        ) : (
                          <span className="flex gap-2">
                            <input value={assign[s.id] || ""} onChange={(e) => setAssign({ ...assign, [s.id]: e.target.value })}
                              placeholder="learner@email.com" data-testid={`seat-assign-input-${s.id}`}
                              className="border mv-hairline rounded px-2 py-1 text-xs w-44" />
                            <button onClick={() => doAssign(s.id)} data-testid={`seat-assign-btn-${s.id}`}
                              className="btn-green px-2 py-1 rounded text-xs font-semibold inline-flex items-center gap-1">
                              <UserPlus size={12} /> Assign
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
              <Clock size={13} /> Learners appear in progress once they register with the same email and start the course.
              Direct them to <Link to="/learn/bundles" className="underline mv-green-text">the bundles page</Link>.
            </p>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
              <Users size={13} /> Need 50+ seats or invoicing? Email Sebata@pulalove.com.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
